import type { ChatUi, GuidedFlow } from '@voll-intelligence/genui-contract';
import { GuidedFlowCards } from '@voll-intelligence/intelligence-ui-kit';
import { useGenUiI18n, type GenUiKey } from '../../shared/ui/i18n.js';
import type { GenUiEntry, GenUiRenderProps } from '../../shared/ui/registry.js';

type GuidedFlowsBlock = Extract<ChatUi, { kind: 'guided_flows' }>;

const FLOW_LABEL: Record<GuidedFlow, GenUiKey> = {
  flight: 'guidedFlows.flow.flight',
  hotel: 'guidedFlows.flow.hotel',
  rental: 'guidedFlows.flow.rental',
  bus: 'guidedFlows.flow.bus',
};

const FLOW_REPLY: Record<GuidedFlow, GenUiKey> = {
  flight: 'guidedFlows.reply.flight',
  hotel: 'guidedFlows.reply.hotel',
  rental: 'guidedFlows.reply.rental',
  bus: 'guidedFlows.reply.bus',
};

/** As mesmas frases COM o viajante, para o pedido que é para outra pessoa
 *  (#2952) — o balão em 1ª pessoa desfazia o contexto de terceiro. */
const FLOW_REPLY_ON_BEHALF: Record<GuidedFlow, GenUiKey> = {
  flight: 'guidedFlows.reply.flight.onBehalf',
  hotel: 'guidedFlows.reply.hotel.onBehalf',
  rental: 'guidedFlows.reply.rental.onBehalf',
  bus: 'guidedFlows.reply.bus.onBehalf',
};

/**
 * `guided_flows` — os atalhos ilustrados na conversa. O card CONDUZ a conversa em
 * vez de tirar o cliente dela: o toque manda a mensagem do serviço escolhido
 * carregando o domínio FORA do texto livre (ação `guided-flow` → `domainHint` no
 * POST /messages), então o supervisor entra direto no subgrafo do domínio sem
 * classificar a prosa. O `text` continua virando o balão visível do usuário.
 */
function GuidedFlows({
  ui,
  dispatch,
  readOnly = false,
}: Readonly<GenUiRenderProps<GuidedFlowsBlock>>) {
  const { t } = useGenUiI18n();
  // Pedido para outra pessoa: o viajante vem no bloco, e a copy sai daqui — o
  // convite fala do serviço PARA ele, e o toque leva o nome junto.
  const traveler = ui.traveler ?? null;
  const reply = traveler === null ? FLOW_REPLY : FLOW_REPLY_ON_BEHALF;
  return (
    <GuidedFlowCards
      label={
        ui.label ??
        (traveler === null ? t('guidedFlows.label') : t('guidedFlows.label.onBehalf', { traveler }))
      }
      flows={ui.flows.map((flow) => ({ key: flow, label: t(FLOW_LABEL[flow]) }))}
      animateIn
      disabled={readOnly}
      onSelect={(flow) => {
        dispatch({ type: 'guided-flow', flow, text: t(reply[flow], { traveler }) });
      }}
    />
  );
}

/** Entrada de catálogo do `guided_flows` — slot `chat`, sem challenge. */
export const guidedFlowsEntry: GenUiEntry<GuidedFlowsBlock> = {
  kind: 'guided_flows',
  slot: 'chat',
  render: (props) => <GuidedFlows {...props} />,
};
